import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  PageHeader,
  Button,
  TextField,
  Segmented,
  Chip,
  DataTable,
  ConfirmDialog,
  IconButton,
  EmptyState,
  Skeleton,
  useSnackbar,
  AddIcon,
  EditIcon,
  DeleteIcon,
  type Column,
} from '@/ui'
import { LINE_KINDS, LINE_KIND_LABELS, type CatalogItem, type LineKind } from '@garage/shared'
import { api } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useDebounced, useFormatters } from '@/lib/hooks.js'
import { CatalogItemDialog } from '@/components/CatalogItemDialog.js'

const PAGE_SIZE = 20

export function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const formatters = useFormatters()
  const { t } = useTranslation()

  // URL state
  const kindParam = (searchParams.get('kind') || '') as LineKind | ''
  const qParam = searchParams.get('q') || ''
  const pageParam = Math.max(1, Number(searchParams.get('page')) || 1)

  // Local state
  const [searchInput, setSearchInput] = useState(qParam)
  const [selectedItem, setSelectedItem] = useState<CatalogItem | undefined>()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<CatalogItem | undefined>()

  const debouncedQ = useDebounced(searchInput, 250)

  // Update URL when search debounces
  const updateSearch = (q: string) => {
    const params = new URLSearchParams(searchParams)
    if (q) {
      params.set('q', q)
    } else {
      params.delete('q')
    }
    params.set('page', '1')
    setSearchParams(params, { replace: true })
  }

  const updateKind = (k: LineKind | '') => {
    const params = new URLSearchParams(searchParams)
    if (k) {
      params.set('kind', k)
    } else {
      params.delete('kind')
    }
    params.set('page', '1')
    setSearchParams(params)
  }

  const updatePage = (page: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', String(page))
    setSearchParams(params)
  }

  // Sync debounced search to URL
  useEffect(() => {
    if (debouncedQ !== qParam) {
      updateSearch(debouncedQ)
    }
  }, [debouncedQ, qParam, searchParams, setSearchParams])

  // Fetch catalog
  const { data, isLoading } = useQuery({
    queryKey: qk.catalog({ kind: kindParam || undefined, q: qParam, page: pageParam, pageSize: PAGE_SIZE }),
    queryFn: () =>
      api.catalog.list({
        kind: kindParam || undefined,
        q: qParam,
        page: pageParam,
        pageSize: PAGE_SIZE,
      }),
  })

  // Delete mutation
  const { mutate: deleteItem, isPending: isDeleting } = useMutation({
    mutationFn: (id: string) => api.catalog.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.catalog() })
      snackbar.success(t('catalog.itemRemoved'))
      setDeleteTarget(undefined)
    },
    onError: () => {
      snackbar.error(t('catalog.failedToRemove'))
    },
  })

  // Dialog handlers
  const handleAddClick = () => {
    setSelectedItem(undefined)
    setDialogOpen(true)
  }

  const handleEditClick = (item: CatalogItem) => {
    setSelectedItem(item)
    setDialogOpen(true)
  }

  const handleDeleteClick = (item: CatalogItem) => {
    setDeleteTarget(item)
  }

  const handleDeleteConfirm = () => {
    if (deleteTarget) {
      deleteItem(deleteTarget.id)
    }
  }

  // Pagination calculations
  const total = data?.total || 0
  const start = data ? (pageParam - 1) * PAGE_SIZE + 1 : 0
  const end = data ? Math.min(pageParam * PAGE_SIZE, total) : 0
  const hasNextPage = data ? pageParam * PAGE_SIZE < total : false
  const hasPrevPage = pageParam > 1

  // Empty state
  const isEmpty = !isLoading && (!data?.items || data.items.length === 0)
  const isSearchEmpty = isEmpty && (qParam || kindParam)

  const lineKindLabel = (kind: LineKind) => t(`catalog.kinds.${kind}`, LINE_KIND_LABELS[kind])

  // DataTable columns
  const columns: Column<CatalogItem>[] = [
    {
      key: 'name',
      header: t('catalog.columns.name'),
      cell: (item: CatalogItem) => item.name,
    },
    {
      key: 'kind',
      header: t('catalog.columns.kind'),
      cell: (item: CatalogItem) => <Chip label={lineKindLabel(item.kind)} />,
      width: 'w-28',
    },
    {
      key: 'partNumber',
      header: t('catalog.columns.partNumber'),
      cell: (item: CatalogItem) => item.partNumber ? <span className="ref">{item.partNumber}</span> : '—',
      width: 'w-32',
      hideOnMobile: true,
    },
    {
      key: 'cost',
      header: t('catalog.columns.ourCost'),
      cell: (item: CatalogItem) => {
        if (item.defaultCostMinor === null) return '—'
        return <span className="text-on-surface-variant">{formatters.amount(item.defaultCostMinor)}</span>
      },
      numeric: true,
      width: 'w-28',
      hideOnMobile: true,
    },
    {
      key: 'rate',
      header: t('catalog.columns.clientRate'),
      cell: (item: CatalogItem) => formatters.amount(item.defaultRateMinor),
      numeric: true,
      width: 'w-28',
    },
    {
      key: 'timesUsed',
      header: t('catalog.columns.used'),
      cell: (item: CatalogItem) => item.timesUsed,
      numeric: true,
      width: 'w-20',
      hideOnMobile: true,
    },
    {
      key: 'actions',
      header: '',
      cell: (item: CatalogItem) => (
        <div className="flex gap-1">
          <IconButton
            label={t('common.edit')}
            icon={<EditIcon />}
            variant="standard"
            size="sm"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              handleEditClick(item)
            }}
          />
          <IconButton
            label={t('common.delete')}
            icon={<DeleteIcon />}
            variant="danger"
            size="sm"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              handleDeleteClick(item)
            }}
          />
        </div>
      ),
      width: 'w-24',
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('catalog.title')}
        subtitle={t('catalog.subtitle')}
        actions={
          <Button variant="filled" icon={<AddIcon />} onClick={handleAddClick}>
            {t('catalog.addItem')}
          </Button>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Kind filter using Segmented control */}
        <Segmented
          options={[
            { value: '', label: t('status.all') },
            ...LINE_KINDS.map((k) => ({
              value: k,
              label: lineKindLabel(k),
            })),
          ]}
          value={kindParam}
          onChange={updateKind}
          label={t('catalog.filterByKind')}
          scrollable
        />

        {/* Search */}
        <TextField
          label={t('common.search')}
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('catalog.searchPlaceholder')}
          className="max-w-xs"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton lines={5} />
        </div>
      ) : isEmpty ? (
        <EmptyState
          title={isSearchEmpty ? t('catalog.noItemsFound') : t('catalog.noEntriesYet')}
          description={
            isSearchEmpty
              ? t('catalog.adjustFilters')
              : t('catalog.subtitle')
          }
        />
      ) : data ? (
        <div className="space-y-4">
          <div className="bg-surface-container border border-outline-variant rounded-lg">
            <DataTable<CatalogItem>
              columns={columns}
              rows={data.items}
              rowKey={(item) => item.id}
              stackOnMobile
            />
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <div className="text-body-sm text-on-surface-variant">
              {t('common.showingRange', { from: start, to: end, total })}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outlined"
                disabled={!hasPrevPage}
                onClick={() => updatePage(pageParam - 1)}
              >
                {t('common.previous')}
              </Button>
              <Button
                variant="outlined"
                disabled={!hasNextPage}
                onClick={() => updatePage(pageParam + 1)}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Dialogs */}
      <CatalogItemDialog
        open={dialogOpen}
        item={selectedItem}
        onClose={() => setDialogOpen(false)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('catalog.removeItemTitle')}
        message={t('catalog.removeItemMessage')}
        confirmLabel={t('common.remove')}
        destructive
        loading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(undefined)}
      />
    </div>
  )
}
